import { useState, useCallback, useMemo, useEffect } from "react";
import { SUG_MUTZAR_CATEGORY, SUG_MUTZAR_ICON } from "../utils/fieldMaps";
import { shortenCompanyName, normalizeId } from "../utils/formatters";
import { memoryStore } from "../utils/storage";
import {
  recordSnapshot,
  recordSnapshotsBatch,
  removeClientHistory,
  removeClientHistoriesBatch,
  pruneMonthsAfter,
} from "../utils/holdingsHistory";
import { useAuth } from "../contexts/AuthContext";

const STORAGE_KEY = "pension_holdings_v1";

const normPol = (p) => String(p || "").replace(/[^a-zA-Z0-9]/g, "").replace(/^0+/, "") || "0";

// מפתח דדופ לקרן — מתעלם מתווים שאינם אלפא-נומריים ומאפסים מובילים במספר הפוליסה
function fundKey(f) {
  if (f._manual && f.id) return f.id; // Preserve ID for manual products
  return `${f.companyCode || ""}_${normPol(f.polNum)}_${f.kidodAchid || ""}`;
}

function readStore() {
  try {
    const raw = memoryStore.getItem(STORAGE_KEY);
    if (!raw) return [];
    
    const parsed = JSON.parse(raw);
    
    // One-time auto-migration to fix corrupted funds that lack an ID or statusLabel
    let modified = false;
    parsed.forEach(c => {
      c.clientId = normalizeId(c.clientId);
      if (c.funds) {
        c.funds.forEach((f, idx) => {
          if (!f.statusLabel) {
            f.statusLabel = "פעיל";
            modified = true;
          }
          if (!f.id) {
            // Use stable ID format to match MigdalImport and fundKey logic
            f.id = `${f.companyCode || ""}_${f.polNum || ""}_${f.kidodAchid || ""}`;
            modified = true;
          }
        });
      }
      if (c.sources) {
        c.sources.forEach(s => {
          if (!s.companyCode || s.companyCode === "undefined" || s.companyCode === "null") {
            // Check if there are any Migdal funds for this client
            const hasMigdalFunds = (c.funds || []).some(f => f.companyCode === "10" || f.companyName === "מגדל");
            if (hasMigdalFunds || (s.fileName && (s.fileName.toLowerCase().includes("life") || s.fileName.includes("מגדל")))) {
              s.companyCode = "10";
              modified = true;
            }
          }
        });
      }
    });
    
    if (modified) {
      try {
        memoryStore.setItem(STORAGE_KEY, JSON.stringify(parsed));
      } catch (e) {
        console.error("Failed to auto-migrate holdings", e);
      }
    }
    
    return parsed;
  } catch {
    return [];
  }
}

export function useHoldingsStore() {
  const { syncVersion } = useAuth();

  const [rawClients, setRawClients] = useState(readStore);

  // מיפוי cat/icon/companyName כמו במרחב טרום-ייעוץ, לתאימות UI
  const clients = useMemo(() => {
    return rawClients.map((c) => {
      const funds = (c.funds || []).map((f) => ({
        ...f,
        cat: SUG_MUTZAR_CATEGORY[f.sugMutzar] || f.cat,
        icon: SUG_MUTZAR_ICON[f.sugMutzar] || f.icon,
        companyName: shortenCompanyName(f.companyName),
      }));
      return {
        ...c,
        id: normalizeId(c.clientId),
        clientId: normalizeId(c.clientId),
        funds,
        fileInfo: {
          client: c.clientName,
          clientId: normalizeId(c.clientId),
          companies: c.companies || [],
          fundsCount: funds.length,
          taarichBitzua: c.updatedAt || null,
          isHoldings: true,
        },
      };
    });
  }, [rawClients]);

  // רענון בעקבות סנכרון ענן (event-based + syncVersion) או עדכון לוקאלי מקומפוננטה אחרת
  useEffect(() => {
    const onUpdate = () => setRawClients(readStore());
    window.addEventListener("pension-data-synced", onUpdate);
    window.addEventListener("pension-holdings-updated", onUpdate);
    return () => {
      window.removeEventListener("pension-data-synced", onUpdate);
      window.removeEventListener("pension-holdings-updated", onUpdate);
    };
  }, []);

  useEffect(() => {
    if (syncVersion > 0) setRawClients(readStore());
  }, [syncVersion]);

  const persist = useCallback((next) => {
    try {
      memoryStore.setItem(STORAGE_KEY, JSON.stringify(next));
      window.dispatchEvent(new Event("pension-holdings-updated"));
    } catch (e) {
      console.error("Failed to save holdings to memoryStore", e);
    }
  }, []);

  // קליטת קרנות מקובץ ZIP אחד (חברה אחת, הרבה לקוחות).
  // מקבצת לפי ת"ז, וממזגת קרנות לכרטיס לקוח אחד (union + dedupe).
  //
  // חשוב: המיזוג וה-persist רצים סינכרונית מחוץ ל-setState updater.
  // כשהקריאה מגיעה ממודל שנסגר מיד אחרי האישור (לשל MigdalImport),
  // updater שמתוזמן על קומפוננטה שמתפרקת לא ירוץ לעולם — וה-persist
  // שהיה בתוכו לא היה קורה. קוראים מ-readStore() (מקור האמת) ושומרים מיד.
  // opts.replaceExisting — קרן שכבר קיימת (אותו fundKey) מוחלפת בנתונים החדשים
  // במקום להידלג. נדרש לעדכון חודשי של תיק מנוהל (אותו id, ערכים חדשים).
  const addHoldingsFunds = useCallback(
    (funds, fileMeta = {}, opts = {}) => {
      if (!funds || !funds.length) return { clientsTouched: 0, fundsAdded: 0 };

      // קיבוץ קרנות לפי ת"ז
      const byClient = new Map();
      for (const f of funds) {
        let cid = normalizeId(f.client?.id);
        if (!cid || cid === "—") {
            cid = "000000000"; // fallback for missing client IDs
        }
        if (!byClient.has(cid)) byClient.set(cid, []);
        byClient.get(cid).push(f);
      }

      let fundsAdded = 0;
      let stateModified = false;
      {
        const prev = readStore();
        const map = new Map(prev.map((c) => [normalizeId(c.clientId), { ...c }]));

        for (const [cid, clientFunds] of byClient.entries()) {
          const first = clientFunds[0];
          const existing = map.get(cid);
          const companyCode = first.companyCode;
          const companyName = first.companyName;

          if (existing) {
            const seen = new Set((existing.funds || []).map(fundKey));
            const merged = [...(existing.funds || [])];
            let fundsAddedForClient = 0;
            let hadMissingProperties = false;
            
            for (const f of clientFunds) {
              const k = fundKey(f);
              if (seen.has(k)) {
                if (opts.replaceExisting) {
                  const idx = merged.findIndex(ef => fundKey(ef) === k);
                  if (idx >= 0) {
                    const ef = merged[idx];
                    const existingIsZip = ef._sourceFileName && ef._sourceFileName.toLowerCase().endsWith(".zip");
                    const newIsZip = fileMeta.fileName && fileMeta.fileName.toLowerCase().endsWith(".zip");
                    
                    // מניעת דריסה: קובץ מסלקה (ZIP) מקבל תמיד עדיפות על פני קובץ יצרן (DAT)
                    if (existingIsZip && !newIsZip) {
                      continue; 
                    }
                    
                    merged[idx] = { ...f, _sourceFileName: fileMeta.fileName, _sourceType: fileMeta.sourceType };
                    hadMissingProperties = true; // מפעיל את מסלול ה-persist+snapshot הקיים
                  }
                  continue;
                }
                // If the fund exists but has no source file name, update it
                const existingFund = merged.find(ef => fundKey(ef) === k);
                if (existingFund) {
                  if (!existingFund._sourceFileName && fileMeta.fileName) {
                    existingFund._sourceFileName = fileMeta.fileName;
                    hadMissingProperties = true;
                  }
                  if (!existingFund._sourceType && fileMeta.sourceType) {
                    existingFund._sourceType = fileMeta.sourceType;
                    hadMissingProperties = true;
                  }
                  if (!existingFund.statusLabel) {
                    existingFund.statusLabel = "פעיל";
                    hadMissingProperties = true;
                  }
                  if (!existingFund.id) {
                    existingFund.id = k;
                    hadMissingProperties = true;
                  }
                }
                continue;
              }
              seen.add(k);
              merged.push({ ...f, _sourceFileName: fileMeta.fileName, _sourceType: fileMeta.sourceType });
              fundsAddedForClient++;
              fundsAdded++;
            }
            const companies = existing.companies || [];
            if (companyCode && !companies.some((co) => co.code === companyCode)) {
              companies.push({ code: companyCode, name: companyName });
            }
            
            // Deduplicate sources
            let nextSources = [...(existing.sources || [])];
            let addedNewSource = false;
            if (fileMeta?.fileName && !nextSources.some(s => s.fileName === fileMeta.fileName)) {
              // If replacing existing, remove older sources that share the same company and month
              if (opts.replaceExisting && fileMeta.shemYatzran && fileMeta.taarichBitzua) {
                const period = String(fileMeta.taarichBitzua).slice(0, 7);
                nextSources = nextSources.filter(s => 
                  !(s.shemYatzran === fileMeta.shemYatzran && String(s.taarichBitzua).slice(0, 7) === period)
                );
              }
              nextSources.push({
                fileName: fileMeta.fileName,
                companyCode: fileMeta.companyCode || companyCode,
                shemYatzran: fileMeta.shemYatzran || companyName || null,
                taarichBitzua: fileMeta.taarichBitzua,
                sourceType: fileMeta.sourceType,
                addedAt: new Date().toISOString()
              });
              addedNewSource = true;
            }

            if (fundsAddedForClient > 0 || addedNewSource || hadMissingProperties) {
               map.set(cid, {
                 ...existing,
                 clientName: existing.clientName || first.client?.name || "לקוח",
                 client: existing.client || first.client || null,
                 funds: merged,
                 companies,
                 sources: nextSources,
                 updatedAt: new Date().toISOString(),
               });
               stateModified = true;
            }
          } else {
            fundsAdded += clientFunds.length;
            map.set(cid, {
              clientId: cid,
              clientName: first.client?.name || "לקוח",
              client: first.client || null,
              funds: clientFunds.map(f => ({ ...f, _sourceFileName: fileMeta.fileName, _sourceType: fileMeta.sourceType })),
              companies: companyCode ? [{ code: companyCode, name: companyName }] : [],
              sources: fileMeta?.fileName ? [
                { fileName: fileMeta.fileName, companyCode: fileMeta.companyCode || companyCode, shemYatzran: fileMeta.shemYatzran || companyName || null, taarichBitzua: fileMeta.taarichBitzua, sourceType: fileMeta.sourceType, addedAt: new Date().toISOString() },
              ] : [],
              updatedAt: new Date().toISOString(),
            });
            stateModified = true;
          }
        }

        if (stateModified) {
          const next = Array.from(map.values());
          persist(next);          // memoryStore + event — שאר ה-instances מתרעננים
          setRawClients(next);    // עדכון ה-instance המקומי (ללא side effects בתוך updater)

          // record/replace this month's history snapshot per touched client,
          // using the FULL merged fund set across all companies
          const updates = [];
          for (const cid of byClient.keys()) {
            const merged = map.get(cid);
            if (merged?.funds?.length) {
              updates.push({ clientId: cid, funds: merged.funds, fileMeta: { fileName: fileMeta.fileName, taarichBitzua: fileMeta.taarichBitzua } });
            }
          }
          if (updates.length > 0) recordSnapshotsBatch(updates);
        }
      }

      return { clientsTouched: byClient.size, fundsAdded, clientIds: Array.from(byClient.keys()) };
    },
    [persist]
  );

  // ── Manual products (advisor-entered funds, e.g. פק"מ / bank portfolio) ──────
  // Stored inside the client's funds array like any other fund, so they flow
  // into totals, charts, exposure and — via recordSnapshot below — the monthly
  // history. They carry _manual:true and no _sourceFileName, so removeHoldingsFile
  // never touches them.
  const snapshotClient = useCallback((cid, client) => {
    if (client?.funds?.length) {
      // Place the snapshot in the value-date month of the newest fund we can date.
      const dated = client.funds.find(f => f.validDate) || client.funds[0];
      recordSnapshot(cid, client.funds, { taarichBitzua: dated?.validDate });
    }
  }, []);

  const addManualFund = useCallback((clientId, fund) => {
    const cid = normalizeId(clientId);
    const next = readStore();
    const client = next.find(c => normalizeId(c.clientId) === cid);
    if (!client) return; // manual products only on existing holdings clients
    client.funds = [...(client.funds || []), fund];
    client.updatedAt = new Date().toISOString();
    persist(next);
    setRawClients(next);
    snapshotClient(cid, client);
  }, [persist, snapshotClient]);

  const updateManualFund = useCallback((clientId, fundId, patch) => {
    const cid = normalizeId(clientId);
    const next = readStore();
    const client = next.find(c => normalizeId(c.clientId) === cid);
    if (!client) return;
    client.funds = (client.funds || []).map(f =>
      (f._manual && f.id === fundId) ? { ...f, ...patch } : f
    );
    client.updatedAt = new Date().toISOString();
    persist(next);
    setRawClients(next);
    snapshotClient(cid, client);
  }, [persist, snapshotClient]);

  const removeFund = useCallback((clientId, fundId) => {
    const cid = normalizeId(clientId);
    const next = readStore();
    const client = next.find(c => normalizeId(c.clientId) === cid);
    if (!client) return;
    client.funds = (client.funds || []).filter(f => f.id !== fundId && fundKey(f) !== fundId);
    client.updatedAt = new Date().toISOString();
    persist(next);
    setRawClients(next);
    snapshotClient(cid, client);
  }, [persist, snapshotClient]);

  const removeHoldingsClient = useCallback(
    (clientId) => {
      // Read from readStore() (source of truth) + persist synchronously — same
      // rationale as addHoldingsFunds: a closure over rawClients can be stale
      // across rapid successive removals and re-persist already-removed data.
      const next = readStore().filter((c) => c.clientId !== clientId);
      persist(next);
      setRawClients(next);
      removeClientHistory(clientId);
    },
    [persist]
  );

  const clearHoldings = useCallback(() => {
    setRawClients([]);
    setTimeout(() => persist([]), 0);
  }, [persist]);

  const removeHoldingsFile = useCallback(
    (fileName) => {
      // Read from readStore() (source of truth) + persist synchronously — a
      // closure over rawClients can be stale across rapid successive removals
      // and silently re-persist a file that was just removed (same rationale as
      // addHoldingsFunds).
      let changed = false;
      const removedIds = [];
      const touchedIds = [];     // only clients actually affected by THIS file
      const next = readStore().map(c => {
        const filteredFunds   = (c.funds   || []).filter(f => f._sourceFileName !== fileName);
        const filteredSources = (c.sources || []).filter(s => s.fileName        !== fileName);
        if (filteredFunds.length === (c.funds || []).length &&
            filteredSources.length === (c.sources || []).length) return c;
        changed = true;
        touchedIds.push(c.clientId);
        return { ...c, funds: filteredFunds, sources: filteredSources };
      }).filter(c => {
        if (!c.funds || c.funds.length === 0) { removedIds.push(c.clientId); return false; }
        return true;
      });

      if (changed) {
        if (removedIds.length > 0) removeClientHistoriesBatch(removedIds);
        persist(next);
        setRawClients(next);
        // Re-snapshot ONLY the clients touched by this file (and still present).
        const survivors = new Set(next.map(c => c.clientId));
        const removedSet = new Set(removedIds);
        const now = new Date().toISOString();
        const updates = [];
        for (const cid of touchedIds) {
          if (removedSet.has(cid) || !survivors.has(cid)) continue;
          const c = next.find(x => x.clientId === cid);
          if (c?.funds?.length) {
            updates.push({ clientId: cid, funds: c.funds, fileMeta: { fileName: "SYSTEM_REMOVAL", taarichBitzua: now } });
          }
        }
        if (updates.length > 0) recordSnapshotsBatch(updates);
      }
    },
    [persist]
  );

  // ניקוי חודשים שגויים בהיסטוריית האחזקות: לכל לקוח מחשבים את חודש הנכונות
  // האמיתי מהקרנות (TAARICH-NECHONUT / תאריך ערך יתרות) ומסירים snapshots
  // מאוחרים ממנו (ארטיפקטים של תאריך הפקה/תיבה, כמו יוני שנוצר מקובץ שהופק ביוני
  // אבל נכונותו מאי). מחזיר סיכום { clientsAffected, monthsRemoved, periods }.
  const cleanStaleMonths = useCallback(() => {
    const toIso = (d) => {
      const m = String(d || "").match(/(\d{2})\/(\d{2})\/(\d{4})/);
      return m ? `${m[3]}-${m[2]}-${m[1]}` : null;
    };
    const cutoffByClient = {};
    for (const c of readStore()) {
      let bestIso = null;
      for (const f of (c.funds || [])) {
        for (const d of [f.validDate, f.yitrot?.date]) {
          const iso = toIso(d);
          if (iso && (!bestIso || iso > bestIso)) bestIso = iso;
        }
      }
      if (bestIso) cutoffByClient[c.clientId] = bestIso.slice(0, 7); // "YYYY-MM"
    }
    const removed = pruneMonthsAfter(cutoffByClient);
    const monthsRemoved = removed.reduce((s, r) => s + r.periods.length, 0);
    const periods = [...new Set(removed.flatMap((r) => r.periods))].sort();
    return { clientsAffected: removed.length, monthsRemoved, periods };
  }, []);

  return { holdingsClients: clients, addHoldingsFunds, removeHoldingsClient, clearHoldings, removeHoldingsFile, cleanStaleMonths, addManualFund, updateManualFund, removeFund };
}
